import useTranslate from 'hooks/useTranslate';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useChange2FAStatusMutation, useVerify2FAStatusMutation } from 'services';
import useAuthStore, { setUserInfo } from 'store/auth';
import OTPInput from 'react-otp-input';
import useCommonStore from 'store/common';
import { isMobile } from 'react-device-detect';

const TwoFactorAuthentication: React.FC<any> = () => {
	const { translate } = useTranslate();
	const [error, setError] = useState<string | null>(null);
	const [otp, setOtp] = useState('');
	const [timer, setTimer] = useState(30);
	const [isTimerRunning, setIsTimerRunning] = useState(false);
	const { hideCommonModal }: any = useCommonStore((state) => state);

	// mutations
	const { mutateAsync: verifyOTPMutation } = useVerify2FAStatusMutation();
	const { mutateAsync: resend2FAOtp } = useChange2FAStatusMutation();
	const { user, token }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const handleSubmit = async (e: any) => {
		e.preventDefault();
		if (otp.length !== 4) {
			setError(translate('ToastMessage.pleaseEnterOtp'));
			return;
		}
		setError(null);
		const updatedAuth2Factor = !user?.auth2Factor;
		verifyOTPMutation({ otp: otp, set2fa: updatedAuth2Factor }).then((res: any) => {
			if (res?.success) {
				const updateStatue = {
					auth2Factor: updatedAuth2Factor,
					isPhoneNumberVerified: !user?.isPhoneNumberVerified,
				};
				setUserInfo({
					token: token,
					user: {
						...user,
						...updateStatue,
					},
				});
				toast.success(res?.message);
				hideCommonModal();
			}
		});
	};

	const handleRequestOtp = async () => {
		resend2FAOtp({ set2fa: true });
		setTimer(30);
		setIsTimerRunning(true);
	};

	useEffect(() => {
		const keyDownHandler = (event: any) => {
			if (event.key === 'Enter') {
				handleSubmit(event);
			}
		};

		document.addEventListener('keydown', keyDownHandler);

		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
	}, [otp]);

	useEffect(() => {
		let countdown: any = null;

		if (isTimerRunning) {
			countdown = setInterval(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
		}

		return () => clearInterval(countdown);
	}, [isTimerRunning]);

	useEffect(() => {
		if (timer === 0) {
			setIsTimerRunning(false);
		}
	}, [timer]);

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<div className="auth-form mx-auto my-5 my-md-5">
				<div className="auth-form-heading text-center">
					<h2 className="mb-3">
						{translate('Modal.twoFactor')}{' '}
						<span className="fw-bold">
							<br></br>
							{translate('Modal.auth')}
						</span>
					</h2>
					<p className="mb-0">{translate('Modal.typeReceivedOtp')}</p>
				</div>
				<div className="auth-form-fields">
					<Form className="form" onSubmit={handleSubmit}>
						<div className="otp-box position-relative">
							<OTPInput
								value={otp}
								onChange={setOtp}
								numInputs={4}
								renderSeparator={<span>-</span>}
								renderInput={(props) => <input {...props} />}
								inputType={isMobile ? 'number' : 'text'}
							/>
							{error && <div className="f-error text-danger">{error}</div>}
						</div>
						<div className="form-btn">
							<Button className="filled-btn rounded-pill w-100" type="submit">
								{translate('Common.submit')}
							</Button>
						</div>
						<p className="mb-0 text-center">
							{translate('Common.didNotReceiveOtp')}{' '}
							<Button
								variant="link"
								onClick={() => {
									if (!isTimerRunning) {
										setIsTimerRunning(true);
									}
									handleRequestOtp();
								}}
								disabled={isTimerRunning}
								className="fw-bold join-link p-0 border-0"
							>
								{isTimerRunning
									? `${translate('Modal.waitFor')} ${timer} ${translate('Modal.seconds')}`
									: translate('Modal.resend')}
							</Button>
						</p>
					</Form>
				</div>
			</div>
		</Modal.Body>
	);
};

export default TwoFactorAuthentication;
